// <fp-proxy-form> wraps a <form> that posts JSON to an App Proxy endpoint (/apps/fp/*).
// Optional data-note-fields="a,b" folds extra fields into a single `note` string (used by suggest).
// States: [data-state="idle|sending|done|error"] on the element; messages go to [data-fp-status] (aria-live).
class FpProxyForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.status = this.querySelector('[data-fp-status]');
    this.button = this.form?.querySelector('[type="submit"]');
    this.form?.addEventListener('submit', (e) => this.submit(e));
    this.dataset.state = 'idle';
  }

  payload() {
    const data = Object.fromEntries(new FormData(this.form));
    const noteFields = (this.dataset.noteFields || '').split(',').filter(Boolean);
    if (noteFields.length) {
      const parts = [];
      for (const name of noteFields) {
        const field = this.form.elements[name];
        const value = data[name];
        delete data[name];
        if (!value) continue;
        const label = field?.dataset.noteLabel || name;
        parts.push(field?.type === 'checkbox' ? label : `${label}: ${value}`);
      }
      if (parts.length) data.note = parts.join('. ');
    }
    return data;
  }

  setStatus(state, message) {
    this.dataset.state = state;
    if (this.status) this.status.textContent = message || '';
    if (this.button) this.button.disabled = state === 'sending';
  }

  async submit(event) {
    event.preventDefault();
    if (!this.form.reportValidity()) return;
    this.setStatus('sending', this.dataset.sending || 'Sending…');
    try {
      const res = await fetch(this.form.action, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(this.payload()),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.ok === false) {
        const reason = body.error ? `${body.error.charAt(0).toUpperCase()}${body.error.slice(1)}${/[.?!]$/.test(body.error) ? '' : '.'}` : '';
        throw new Error(reason || this.dataset.error);
      }
      this.form.hidden = true;
      this.setStatus('done', this.dataset.success || body.message);
      this.status?.focus();
    } catch (err) {
      this.setStatus('error', err.message && err.message !== 'Failed to fetch' ? err.message : this.dataset.error);
    }
  }
}

if (!customElements.get('fp-proxy-form')) customElements.define('fp-proxy-form', FpProxyForm);
